import { useQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { WorkspaceAccessibilityResponse } from "../WorkspaceAccessibility";

/**
 * Query key for workspace accessibility queries.
 */
export function getWorkspaceAccessibilityQueryKey(workspaceId: string) {
  return ["workspaceAccessibility", workspaceId];
}

/**
 * Hook to fetch the accessibility status of a workspace and its datasets.
 *
 * @param workspaceId - The ID of the workspace to fetch accessibility for
 * @returns React Query result with WorkspaceAccessibilityResponse
 */
export function useWorkspaceAccessibility(workspaceId: string | undefined) {
  const { workspaceService } = useDomainServices();

  return useQuery<WorkspaceAccessibilityResponse, Error>({
    queryKey: getWorkspaceAccessibilityQueryKey(workspaceId ?? ""),
    queryFn: ({ signal }) => {
      if (workspaceId === undefined) {
        return Promise.reject(new Error("Invalid workspaceId"));
      }

      return workspaceService.getWorkspaceAccessibility(workspaceId, {
        abortSignal: signal,
      });
    },
    enabled: workspaceId !== undefined,
    // Always refetch when the modal opens so the user sees current data
    staleTime: 0,
  });
}
